// ===== World 6: The hen yard – HEAVY AND LIGHT + THE SEESAW =====
// The hero is "the Egg" (a sweet egg with a face). The goal is the nest (or an egg tray).
// Principle: heavy goes down, light goes up. Where the weight lands on the seesaw decides how high the egg is thrown.
// Heavy can throw light – a feather can hardly move anything.
import { definePart, defineObj, defineWorld } from '../../core/registry';
import type Matter from 'matter-js';
import type {
  Ctx2D, Gfx, GameBody, IntroHelpers, ObjData, PhysicsApi, Placement, SimState, SkinDef, SoundFn, Static, Thing, WorldDef, WorldMeta,
} from '../../core/types';
import { scene } from './scene';
import { levels } from './levels';
import { strings } from './strings';
import { K, DEG } from './shared';

const INK = '#3B2A5C';
const KICK = 0.4, VG = 0.2778;

/** The seesaw's placement fields: length, board offset from the pivot, angle limit, tilt when flipped, trestle height, mass, kick strength. */
type SeesawPl = Placement & { len?: number; off?: number; lim?: number; tilt?: number; sup?: number; mass?: number; kick?: number };
/** Roof and beam: width of the object. */
type WideObj = ObjData & { w: number };
/** Perch: dir -1 = the hen sits to the right of the egg. */
type PerchObj = ObjData & { dir?: number };
/** The bowl goal as the world draws it; style 'tray' = egg tray (set in setup from the level's goal). */
interface BowlLook { x: number; y: number; w: number; style?: unknown }
/** Egg rider on the seesaw: position along (lx) and across (ly) the board. */
interface Rider { lx: number; ly: number }
/** A dynamic thing (sack, feather, hen) that draws itself. */
interface DynThing extends Thing { b: GameBody }
interface SeesawThing extends Thing { b: GameBody; pl: Placement; L: number; off: number; sup: number; readonly th: number }

// ---------- drawing helpers ----------
// Local outline: solid INK, unlike draw-helpers outline (default colour has .9 alpha).
function outline(ctx: Ctx2D, w?: number) { ctx.lineWidth = w || 3; ctx.strokeStyle = INK; ctx.stroke(); }
// Local selBox: uses ctx.roundRect without save/restore, unlike draw-helpers selBox (rr + save/restore).
function selBox(ctx: Ctx2D, x: number, y: number, w: number, h: number, r?: number) { ctx.beginPath(); ctx.roundRect ? ctx.roundRect(x, y, w, h, r || 10) : ctx.rect(x, y, w, h); ctx.strokeStyle = '#FFC93C'; ctx.lineWidth = 4; ctx.stroke(); }
function dashDown(ctx: Ctx2D, x: number, y: number) { ctx.save(); ctx.setLineDash([4, 10]); ctx.lineCap = 'round'; ctx.lineWidth = 4; ctx.strokeStyle = 'rgba(59,42,92,.25)'; ctx.beginPath(); ctx.moveTo(x, y); ctx.lineTo(x, 800); ctx.stroke(); ctx.restore(); }

// The egg: white/cream egg with a face (helper = little chick)
function eggDraw(ctx: Ctx2D, b: GameBody, kind: string, t: number, g: Gfx) {
  const { x, y } = b.position, r = b.circleRadius!, v = b.velocity || { x: 0, y: 0 };
  const sp = Math.hypot(v.x, v.y);
  ctx.save(); ctx.translate(x, y);
  if (kind === 'helper') {   // chick: yellow fluff ball
    g.shadowOn(ctx, 6, 4);
    ctx.beginPath(); for (let i = 0; i < 14; i++) { const a = i / 14 * Math.PI * 2, rr = r * (i % 2 ? 1.02 : 1.12); ctx.lineTo(Math.cos(a) * rr, Math.sin(a) * rr); } ctx.closePath();
    ctx.fillStyle = '#FFE14D'; ctx.fill(); g.shadowOff(ctx); outline(ctx, 2.5);
    ctx.fillStyle = INK; [-1, 1].forEach(s => { ctx.beginPath(); ctx.arc(s * r * .32, -r * .15, r * .13, 0, 7); ctx.fill(); });
    ctx.beginPath(); ctx.moveTo(-r * .2, r * .08); ctx.lineTo(r * .2, r * .08); ctx.lineTo(0, r * .38); ctx.closePath(); ctx.fillStyle = '#FF9A3D'; ctx.fill();
    ctx.restore(); return;
  }
  ctx.rotate((b.roll || 0) * 0.5);
  g.shadowOn(ctx, 8, 5);
  const gr = ctx.createRadialGradient(-r * .35, -r * .45, r * .1, 0, 0, r * 1.2);
  gr.addColorStop(0, '#FFFFFF'); gr.addColorStop(0.55, '#FFF3DC'); gr.addColorStop(1, '#F0CFA0');
  ctx.beginPath(); ctx.ellipse(0, -r * .06, r * .96, r * 1.1, 0, 0, 7); ctx.fillStyle = gr; ctx.fill(); g.shadowOff(ctx);
  outline(ctx, 2.5);
  // freckles
  ctx.fillStyle = 'rgba(200,140,80,.45)';
  [[-.55, -.55, .07], [.5, -.62, .06], [.62, .3, .05], [-.2, -.85, .05]].forEach(([px, py, pr]) => { ctx.beginPath(); ctx.arc(px * r, py * r, pr * r, 0, 7); ctx.fill(); });
  // face (local: eyes have an outline and a highlight, unlike draw-helpers face)
  const lx = Math.max(-2, Math.min(2, v.x * .4)), ly = Math.max(-2, Math.min(2, v.y * .4));
  const scared = sp > 9 || b.sad;
  for (const s of [-1, 1]) {
    ctx.beginPath(); ctx.arc(s * r * .33, -r * .05, r * .24, 0, 7); ctx.fillStyle = '#fff'; ctx.fill(); ctx.lineWidth = 1.2; ctx.strokeStyle = 'rgba(59,42,92,.5)'; ctx.stroke();
    ctx.beginPath(); ctx.arc(s * r * .33 + lx * .6, -r * .05 + ly * .6, r * (scared ? .1 : .15), 0, 7); ctx.fillStyle = INK; ctx.fill();
    ctx.beginPath(); ctx.arc(s * r * .33 + lx * .6 - 1.5, -r * .05 + ly * .6 - 1.5, r * .05, 0, 7); ctx.fillStyle = '#fff'; ctx.fill();
  }
  ctx.strokeStyle = INK; ctx.fillStyle = INK; ctx.lineWidth = 2; ctx.lineCap = 'round';
  if (b.sad) { ctx.beginPath(); ctx.arc(0, r * .6, r * .22, 1.2 * Math.PI, 1.8 * Math.PI); ctx.stroke(); }
  else if (sp > 7) { ctx.beginPath(); ctx.ellipse(0, r * .42, r * .14, r * .2, 0, 0, 7); ctx.fill(); }
  else { ctx.beginPath(); ctx.arc(0, r * .25, r * .25, .15 * Math.PI, .85 * Math.PI); ctx.stroke(); }
  ctx.fillStyle = 'rgba(255,120,150,.5)';
  [-1, 1].forEach(s => { ctx.beginPath(); ctx.ellipse(s * r * .62, r * .28, r * .14, r * .09, 0, 0, 7); ctx.fill(); });
  ctx.beginPath(); ctx.ellipse(-r * .38, -r * .62, r * .2, r * .12, -0.6, 0, 7); ctx.fillStyle = 'rgba(255,255,255,.9)'; ctx.fill();
  ctx.restore();
}

// Fence board (wood with nails) – plank, seesaw, fixed fences
function boardDraw(ctx: Ctx2D, x: number, y: number, a: number, w: number, h: number, sel: boolean, g: Gfx, col?: [string, string]) {
  const c = col || ['#F2C98E', '#C98B4E'];
  g.block(ctx, x, y, w, h, a, c[0], c[1], Math.min(7, h / 2));
  ctx.save(); ctx.translate(x, y); ctx.rotate(a);
  g.rr(ctx, -w / 2, -h / 2, w, h, Math.min(7, h / 2)); outline(ctx, 2.5);
  ctx.strokeStyle = 'rgba(120,70,30,.35)'; ctx.lineWidth = 2;
  ctx.beginPath(); ctx.moveTo(-w / 2 + 12, -1); ctx.bezierCurveTo(-w / 6, -h / 4, w / 8, h / 4, w / 2 - 14, 0); ctx.stroke();
  ctx.fillStyle = '#7C6F8F';
  for (const s of [-1, 1]) { ctx.beginPath(); ctx.arc(s * (w / 2 - 10), 0, 2.6, 0, 7); ctx.fill(); }
  if (sel) selBox(ctx, -w / 2 - 8, -h / 2 - 8, w + 16, h + 16, 10);
  ctx.restore();
}

// Straw bale
function baleDraw(ctx: Ctx2D, x: number, y: number, w: number, h: number, a: number | undefined, g: Gfx) {
  ctx.save(); ctx.translate(x, y); ctx.rotate(a || 0);
  g.shadowOn(ctx, 10, 5);
  g.rr(ctx, -w / 2, -h / 2, w, h, Math.min(12, h / 3)); ctx.fillStyle = g.lg(ctx, -h / 2, h / 2, '#FFE27A', '#E8B23A'); ctx.fill(); g.shadowOff(ctx);
  outline(ctx, 3);
  ctx.save(); g.rr(ctx, -w / 2, -h / 2, w, h, Math.min(12, h / 3)); ctx.clip();
  ctx.strokeStyle = 'rgba(190,130,30,.55)'; ctx.lineWidth = 2;
  for (let py = -h / 2 + 7; py < h / 2; py += 9) { ctx.beginPath(); ctx.moveTo(-w / 2, py); for (let px = -w / 2; px <= w / 2; px += 14) ctx.lineTo(px, py + ((px / 14) % 2 ? 2 : -2)); ctx.stroke(); }
  ctx.restore();
  ctx.fillStyle = '#C0603A';
  if (w > 40) for (const s of [-1, 1]) { ctx.fillRect(s * w * .25 - 3, -h / 2, 6, h); }
  ctx.restore();
}

// Grain sack (heavy); sq = squash when it lands
function sackDraw(ctx: Ctx2D, x: number, y: number, a: number, sel: boolean, g: Gfx, sq?: number) {
  ctx.save(); ctx.translate(x, y); ctx.rotate(a || 0);
  const s = sq || 0;
  g.shadowOn(ctx, 10, 6);
  ctx.beginPath();
  ctx.moveTo(-12, -24); ctx.quadraticCurveTo(-30 - s, -20, -30 - s, 4); ctx.quadraticCurveTo(-31 - s, 24, 0, 24); ctx.quadraticCurveTo(31 + s, 24, 30 + s, 4); ctx.quadraticCurveTo(30 + s, -20, 12, -24); ctx.closePath();
  ctx.fillStyle = g.lg(ctx, -24, 24, '#D9B488', '#A7784C'); ctx.fill(); g.shadowOff(ctx); outline(ctx, 3);
  // top flap and string
  ctx.beginPath(); ctx.moveTo(-12, -24); ctx.lineTo(-16, -34); ctx.lineTo(-4, -28); ctx.lineTo(0, -36); ctx.lineTo(5, -28); ctx.lineTo(16, -34); ctx.lineTo(12, -24); ctx.closePath();
  ctx.fillStyle = '#C99A6A'; ctx.fill(); outline(ctx, 2.5);
  ctx.beginPath(); ctx.moveTo(-13, -24); ctx.lineTo(13, -24); ctx.strokeStyle = '#E8473C'; ctx.lineWidth = 5; ctx.lineCap = 'round'; ctx.stroke();
  // ear of grain on the sack
  ctx.strokeStyle = '#7A4E2A'; ctx.lineWidth = 2.5;
  ctx.beginPath(); ctx.moveTo(0, 16); ctx.lineTo(0, -10); ctx.stroke();
  ctx.fillStyle = '#7A4E2A';
  for (let i = 0; i < 3; i++) for (const sd of [-1, 1]) { ctx.beginPath(); ctx.ellipse(sd * 5, -6 + i * 7, 4.5, 2.5, sd * 0.6, 0, 7); ctx.fill(); }
  // weight line at the bottom
  ctx.strokeStyle = 'rgba(80,50,20,.35)'; ctx.lineWidth = 2;
  ctx.beginPath(); ctx.moveTo(-22, 18); ctx.quadraticCurveTo(0, 23, 22, 18); ctx.stroke();
  if (sel) selBox(ctx, -38, -42, 76, 72, 12);
  ctx.restore();
}

// Feather (very light)
function featherDraw(ctx: Ctx2D, x: number, y: number, a: number, sel: boolean, g: Gfx) {
  ctx.save(); ctx.translate(x, y); ctx.rotate(a || 0);
  g.shadowOn(ctx, 6, 3);
  ctx.beginPath(); ctx.moveTo(-34, 4); ctx.quadraticCurveTo(-10, -18, 34, -6); ctx.quadraticCurveTo(0, 14, -34, 4); ctx.closePath();
  ctx.fillStyle = g.lg(ctx, -14, 10, '#FFFFFF', '#FFD6E6'); ctx.fill(); g.shadowOff(ctx); outline(ctx, 2.5);
  ctx.strokeStyle = 'rgba(59,42,92,.6)'; ctx.lineWidth = 2;
  ctx.beginPath(); ctx.moveTo(-42, 8); ctx.quadraticCurveTo(-6, -4, 32, -5); ctx.stroke();
  ctx.strokeStyle = 'rgba(230,120,160,.6)'; ctx.lineWidth = 1.5;
  for (let i = -3; i <= 3; i++) { ctx.beginPath(); ctx.moveTo(i * 8, -1 - Math.abs(i)); ctx.lineTo(i * 8 + 6, -10 + Math.abs(i)); ctx.moveTo(i * 8, 0); ctx.lineTo(i * 8 + 5, 7 - Math.abs(i) * .5); ctx.stroke(); }
  if (sel) selBox(ctx, -46, -22, 92, 40, 10);
  ctx.restore();
}

// Hen (medium weight): sits with her wings tucked in; flap = flapping (in flight)
function henDraw(ctx: Ctx2D, x: number, y: number, a: number, sel: boolean, g: Gfx, flap: boolean, t: number) {
  ctx.save(); ctx.translate(x, y); ctx.rotate(a || 0);
  g.shadowOn(ctx, 10, 5);
  ctx.beginPath(); ctx.ellipse(0, 4, 32, 24, 0, 0, 7); ctx.fillStyle = g.lg(ctx, -20, 28, '#FFFFFF', '#E9DCCB'); ctx.fill(); g.shadowOff(ctx); outline(ctx, 3);
  // tail
  ctx.beginPath(); ctx.moveTo(26, -6); ctx.quadraticCurveTo(44, -22, 38, 4); ctx.quadraticCurveTo(34, 8, 26, 8); ctx.closePath(); ctx.fillStyle = '#FFFFFF'; ctx.fill(); outline(ctx, 2.5);
  // head
  ctx.beginPath(); ctx.arc(-20, -16, 14, 0, 7); ctx.fillStyle = '#FFFFFF'; ctx.fill(); outline(ctx, 2.5);
  ctx.beginPath(); ctx.moveTo(-26, -29); ctx.arc(-24, -31, 4, Math.PI, 0); ctx.arc(-17, -32, 4.5, Math.PI, 0); ctx.arc(-11, -29, 3.5, Math.PI, .3); ctx.lineTo(-14, -26); ctx.closePath(); ctx.fillStyle = '#FF4F4F'; ctx.fill(); outline(ctx, 2);
  ctx.beginPath(); ctx.moveTo(-33, -17); ctx.lineTo(-43, -13); ctx.lineTo(-33, -10); ctx.closePath(); ctx.fillStyle = '#FFB13D'; ctx.fill(); outline(ctx, 2);
  ctx.beginPath(); ctx.ellipse(-32, -6, 3.5, 5, 0, 0, 7); ctx.fillStyle = '#FF4F4F'; ctx.fill();
  ctx.beginPath(); ctx.arc(-23, -18, 3, 0, 7); ctx.fillStyle = INK; ctx.fill();
  ctx.beginPath(); ctx.arc(-24, -19, 1, 0, 7); ctx.fillStyle = '#fff'; ctx.fill();
  // wing
  const fl = flap ? Math.sin(t * 0.6) * 0.7 - 0.5 : 0;
  ctx.save(); ctx.translate(2, -2); ctx.rotate(fl);
  ctx.beginPath(); ctx.ellipse(8, 4, 16, 10, 0.2, 0, 7); ctx.fillStyle = '#F1E4D2'; ctx.fill(); outline(ctx, 2.2);
  ctx.strokeStyle = 'rgba(160,120,90,.6)'; ctx.lineWidth = 1.5; ctx.beginPath(); ctx.moveTo(0, 6); ctx.lineTo(18, 8); ctx.moveTo(2, 1); ctx.lineTo(20, 3); ctx.stroke();
  ctx.restore();
  // feet only while she flies
  if (flap) { ctx.strokeStyle = '#FFB13D'; ctx.lineWidth = 3; ctx.beginPath(); ctx.moveTo(-6, 26); ctx.lineTo(-8, 34); ctx.moveTo(6, 26); ctx.lineTo(8, 34); ctx.stroke(); }
  if (sel) selBox(ctx, -48, -42, 92, 76, 12);
  ctx.restore();
}

// Fence-board seesaw on a sawhorse. th = angle (rad), L = length, off = board centre relative to the pivot, sup = sawhorse height
function seesawDraw(ctx: Ctx2D, px: number, py: number, th: number, L: number, off: number, sup: number, sel: boolean, g: Gfx) {
  // sawhorse (stands still)
  ctx.save();
  g.shadowOn(ctx, 8, 4);
  ctx.beginPath(); ctx.moveTo(px - 8, py + 2); ctx.lineTo(px - 26, py + sup); ctx.lineTo(px - 14, py + sup); ctx.lineTo(px, py + 14); ctx.lineTo(px + 14, py + sup); ctx.lineTo(px + 26, py + sup); ctx.lineTo(px + 8, py + 2); ctx.closePath();
  ctx.fillStyle = g.lg(ctx, py, py + sup, '#B9875A', '#8A5A34'); ctx.fill(); g.shadowOff(ctx); outline(ctx, 2.5);
  ctx.restore();
  // the board
  const c = Math.cos(th), s = Math.sin(th);
  boardDraw(ctx, px + off * c, py + off * s, th, L, 14, false, g, ['#FF8C6B', '#D9534A']);
  // cups at the ends (small ledges)
  ctx.save(); ctx.translate(px, py); ctx.rotate(th);
  for (const sd of [-1, 1]) { g.rr(ctx, off + sd * (L / 2 - 5) - 5, -28, 10, 24, 4); ctx.fillStyle = '#FFF1DE'; ctx.fill(); outline(ctx, 2); }
  ctx.restore();
  // axle
  ctx.beginPath(); ctx.arc(px, py, 7, 0, 7); ctx.fillStyle = '#FFE36B'; ctx.fill(); outline(ctx, 2.5);
  if (sel) { ctx.save(); ctx.translate(px, py); ctx.rotate(th); selBox(ctx, off - L / 2 - 10, -30, L + 20, 46, 12); ctx.restore(); }
}

// Pumpkin (the kit's stone in this world)
function pumpkinDraw(ctx: Ctx2D, pl: { x: number; y: number; a?: number }, sel: boolean, g: Gfx) {
  const a = (pl.a || 0) * DEG;
  ctx.save(); ctx.translate(pl.x, pl.y); ctx.rotate(a);
  g.shadowOn(ctx, 10, 6);
  ctx.beginPath(); ctx.ellipse(0, 2, 26, 22, 0, 0, 7); ctx.fillStyle = g.lg(ctx, -20, 24, '#FFB04D', '#E2711D'); ctx.fill(); g.shadowOff(ctx); outline(ctx, 3);
  ctx.strokeStyle = 'rgba(160,60,10,.55)'; ctx.lineWidth = 2.5;
  for (const s of [-1, 1]) { ctx.beginPath(); ctx.ellipse(s * 9, 2, 8, 20, 0, 0, 7); ctx.stroke(); }
  g.rr(ctx, -3, -26, 7, 10, 3); ctx.fillStyle = '#5FA346'; ctx.fill(); outline(ctx, 2);
  if (sel) { ctx.beginPath(); ctx.arc(0, 0, 32, 0, 7); ctx.strokeStyle = '#FFC93C'; ctx.lineWidth = 4; ctx.stroke(); }
  ctx.restore();
}

// ---------- intro animations ----------
// Seesaw: a sack falls on the right end -> the egg flies up from the left end.
// (arrow's third argument is unused by the intro helper; it was null, now the same x.)
function seesawIntro(ctx: Ctx2D, t: number, g: Gfx, h: IntroHelpers) {
  const px = 230, py = 210, k = t % 240;
  const sy = k < 50 ? 30 + 0.06 * k * k : 999;
  const th = k < 50 ? -20 * DEG : Math.min(20, -20 + (k - 50) * 8) * DEG;
  seesawDraw(ctx, px, py, th, 240, 0, 60, false, g);
  const ex = px - 105 * Math.cos(th), ey = py - 105 * Math.sin(th) - 24;
  if (k < 50) sackDraw(ctx, px + 95, Math.min(sy, py - 75), 0, false, g);
  else sackDraw(ctx, px + 95 * Math.cos(th) + 4, py + 95 * Math.sin(th) - 32, th, false, g);
  let bx = ex, by = ey;
  if (k > 55) { const f = k - 55; bx = px - 105 * Math.cos(20 * DEG) + f * 1.6; by = Math.min(ey, py - 30 - 9 * f + 0.13 * f * f); if (by > 300) by = 300; }
  g.ball(ctx, h.fakeBall(bx, by, k > 55 ? 3 : 0, k > 55 && k < 120 ? -6 : 0, 15), 'hero', t);
  if (k < 50) h.arrow(px + 95, 30, px + 95, 40);
}
// Sack: the heavy sack falls fast and straight down
function sackIntro(ctx: Ctx2D, t: number, g: Gfx, h: IntroHelpers) {
  const k = t % 120, y = Math.min(220, 40 + 0.05 * k * k);
  g.block(ctx, 230, 260, 300, 16, 0, '#C99668', '#9A6A43', 8);
  sackDraw(ctx, 230, y, 0, false, g, y >= 220 ? 4 : 0);
  h.arrow(330, 60, 330, 80);
}
// Feather: the feather floats down slowly and the seesaw doesn't move
function featherIntro(ctx: Ctx2D, t: number, g: Gfx, h: IntroHelpers) {
  const k = t % 240, y = Math.min(150, 30 + k * 0.7);
  seesawDraw(ctx, 230, 210, -20 * DEG, 240, 0, 60, false, g);
  featherDraw(ctx, 320 + Math.sin(k * 0.05) * 20, y, Math.sin(k * 0.05) * 0.35, false, g);
  g.ball(ctx, h.fakeBall(125, 225, 0, 0, 15), 'hero', t);
}
// Hen: she flutters down and sits: the seesaw tips calmly, the egg is only lifted a little
function henIntro(ctx: Ctx2D, t: number, g: Gfx, h: IntroHelpers) {
  const k = t % 240, y = Math.min(140, 30 + k * 1.2);
  const th = y < 140 ? -20 * DEG : Math.min(20, -20 + (k - 92) * 0.8) * DEG;
  seesawDraw(ctx, 230, 210, th, 240, 0, 60, false, g);
  henDraw(ctx, 230 + 95 * Math.cos(th), Math.min(y, 210 + 95 * Math.sin(th) - 30), th, false, g, y < 140, t);
  g.ball(ctx, h.fakeBall(230 - 105 * Math.cos(th), 210 - 105 * Math.sin(th) - 24, 0, 0, 15), 'hero', t);
}

// ---------- skins ----------
const skins: Record<string, SkinDef> = {
  henhouse_ground(ctx, b, g) {
    const { x, y } = b.position, w = b.dw!, h = b.dh!;
    g.block(ctx, x, y, w, h, b.angle, '#C99668', '#9A6A43', 8);
    ctx.save(); ctx.translate(x, y); ctx.rotate(b.angle);
    g.rr(ctx, -w / 2, -h / 2, w, 12, 6); ctx.fillStyle = '#7CCB5E'; ctx.fill(); ctx.lineWidth = 2; ctx.strokeStyle = 'rgba(59,42,92,.5)'; ctx.stroke();
    ctx.fillStyle = 'rgba(255,240,200,.5)';
    for (let px = -w / 2 + 20; px < w / 2; px += 47) { ctx.beginPath(); ctx.ellipse(px, 6 + (px % 3) * 3, 3, 2, 0, 0, 7); ctx.fill(); }
    ctx.restore();
  },
  henhouse_straw(ctx, b, g) { baleDraw(ctx, b.position.x, b.position.y, b.dw!, b.dh!, b.angle, g); },
  henhouse_fence(ctx, b, g) { boardDraw(ctx, b.position.x, b.position.y, b.angle, b.dw!, b.dh!, false, g); },
  henhouse_post(ctx, b, g) { boardDraw(ctx, b.position.x, b.position.y, b.angle, b.dw!, b.dh!, false, g, ['#D8A16A', '#9E6A3C']); },
  henhouse_house(ctx, b, g) {
    const { x, y } = b.position, w = b.dw!, h = b.dh!;
    g.block(ctx, x, y, w, h, b.angle, '#FF8A7A', '#D9534A', 8);
    ctx.save(); ctx.translate(x, y); ctx.rotate(b.angle);
    g.rr(ctx, -w / 2, -h / 2, w, h, 8); outline(ctx, 2.5);
    ctx.strokeStyle = 'rgba(120,30,30,.35)'; ctx.lineWidth = 2;
    if (h > w) for (let py = -h / 2 + 22; py < h / 2; py += 22) { ctx.beginPath(); ctx.moveTo(-w / 2 + 3, py); ctx.lineTo(w / 2 - 3, py); ctx.stroke(); }
    else for (let px = -w / 2 + 22; px < w / 2; px += 22) { ctx.beginPath(); ctx.moveTo(px, -h / 2 + 3); ctx.lineTo(px, h / 2 - 3); ctx.stroke(); }
    ctx.restore();
  },
};

// ---------- seesaw physics (one degree of freedom, driven by force x arm) ----------
// Sawhorse height: down to the nearest static block below the axle (only for parts from the toolbox)
let curStatics: Static[] = [];
function supFor(pl: SeesawPl) {
  if (pl.sup) return pl.sup;
  let best = 60;
  let bt = 1e9;
  for (const s of curStatics) { if (Math.abs((s.a || 0)) > 3 || Math.abs(pl.x - s.x) > s.w / 2) continue; const top = s.y - s.h / 2; if (top > pl.y + 20 && top < bt) bt = top; }
  return bt < 1e9 ? Math.max(30, Math.min(500, bt - pl.y)) : best;
}
function seesawCreate(api: PhysicsApi, pl: SeesawPl): SeesawThing {
  const L = pl.len || 240, flip = Math.abs(pl.a || 0) > 90, off = (pl.off || 0) * (flip ? -1 : 1), lim = (pl.lim || 20) * DEG, sup = supFor(pl);
  const a0 = flip ? (pl.tilt || 20) * DEG : (Math.abs(pl.a || 0) > 90 ? 0 : (pl.a || 0) * DEG);
  let th = Math.max(-lim, Math.min(lim, flip ? (pl.a! > 0 ? lim : -lim) : a0)), om = 0;
  const px = pl.x, py = pl.y, B = api.Bodies, Body3 = api.Body;
  const pts = [B.rectangle(off, 0, L, 14, { chamfer: { radius: 6 } })];
  pts.push(B.rectangle(off - L / 2 + 5, -16, 10, 24, { chamfer: { radius: 4 } }));
  pts.push(B.rectangle(off + L / 2 - 5, -16, 10, 24, { chamfer: { radius: 4 } }));
  const b = api.Body.create({ parts: pts, isStatic: true, friction: 0.6, restitution: 0.02, label: 'henhouse_seesaw' }) as GameBody;
  for (const q of b.parts) { q.friction = 0.6; q.restitution = 0.02; }
  const c0 = { x: b.position.x, y: b.position.y };
  const place = (upd: boolean) => { const c = Math.cos(th), s = Math.sin(th); Body3.setAngle(b, th, upd); Body3.setPosition(b, { x: px + c0.x * c - c0.y * s, y: py + c0.x * s + c0.y * c }, upd); };
  api.Body.setPosition(b, { x: px + c0.x, y: py + c0.y }); api.Body.setAngle(b, 0); place(false);
  b.kind = 'henhouse_seesaw'; api.add(b);
  // trestle (static block under the axle)
  const leg = api.add(api.R(px, py + 14 + (sup - 14) / 2, 30, sup - 14, { isStatic: true, friction: 0.3 })); leg.kind = 'henhouse_trestle';
  const mb = pl.mass || 10, I0 = mb * L * L / 12 + mb * off * off;
  const arm = (o: GameBody) => { const dx = o.position.x - px, dy = o.position.y - py; return dx * Math.cos(th) + dy * Math.sin(th); };
  const side = (o: GameBody) => { const dx = o.position.x - px, dy = o.position.y - py; return -dx * Math.sin(th) + dy * Math.cos(th); };
  const st: SeesawThing = { b, pl, L, off, sup, get th() { return th; } };
  const kicked = new Set<GameBody>(), riders = new Map<GameBody, Rider>();
  api.onHit(b, o => {
    if (!api.isDynamic(o) || side(o) > 0 || kicked.has(o)) return;
    const n = { x: -Math.sin(th), y: Math.cos(th) };
    const vn = o.velocity.x * n.x + o.velocity.y * n.y;
    if (vn <= 0.3) return;
    const d = Math.max(-L / 2 + off, Math.min(L / 2 + off, arm(o)));
    if (o.mass > 0.5) kicked.add(o);
    const dom = (pl.kick || KICK) * o.mass * Math.min(vn, 7) * Math.sign(d) * Math.sqrt(Math.abs(d) * L / 2) / (I0 + 400 * o.mass);
    om += dom;
    if (Math.abs(dom) > 0.01) for (const r of api.touching(b)) if (r !== o && api.isDynamic(r) && side(r) <= 0 && arm(r) * dom < 0 && r.mass < 15) riders.set(r, { lx: arm(r), ly: side(r) });
    if (o.mass > 4 && vn > 2) api.sfx('henhouse_thud', o.position.x, o.position.y, '#E0B070', 8);
  });
  st.step = () => {
    if (!api.S.started) return;
    let tq = mb * VG * off * Math.cos(th), I = I0;
    for (const o of api.touching(b)) {
      if (!api.isDynamic(o) || side(o) > 0) continue;
      const d = arm(o); tq += o.mass * VG * d * Math.cos(th); I += 0.35 * o.mass * d * d;
    }
    om += tq / I; om *= 0.995;
    if (Math.abs(om) > 0.3) om = Math.sign(om) * 0.3;
    let nt = th + om;
    const rigid = (o: GameBody, q: Rider) => {   // the rider follows the board rigidly (the egg in the cup)
      const c = Math.cos(th), sn = Math.sin(th), dx = q.lx * c - q.ly * sn, dy = q.lx * sn + q.ly * c;
      api.Body.setPosition(o, { x: px + dx, y: py + dy }); api.Body.setVelocity(o, { x: -om * dy, y: om * dx });
    };
    let hit = 0;
    if (nt > lim) { nt = lim; if (om > 0) hit = 1; }
    if (nt < -lim) { nt = -lim; if (om < 0) hit = -1; }
    th = nt; place(true);
    if (riders.size && om * [...riders.values()][0].lx > 0) riders.clear();
    for (const [o, q] of riders) rigid(o, q);      // at the stop: the egg flies on with the board's speed (ω x r)
    if (hit) {
      if (Math.abs(om) >= 0.02) api.sfx('henhouse_clonk', px + Math.cos(th) * L / 2 * hit, py + Math.sin(th) * L / 2 * hit);
      riders.clear(); om = 0;
    }
  };
  st.busy = () => Math.abs(om) > 0.002;
  st.layer = 'mid';
  st.draw = (ctx, t, g) => seesawDraw(ctx, px, py, th, L, off, sup, false, g);
  return st;
}
function seesawPlDraw(ctx: Ctx2D, p: Placement, sel: boolean, g: Gfx) {
  const pl = p as SeesawPl;
  const flip = Math.abs(pl.a || 0) > 90, off = (pl.off || 0) * (flip ? -1 : 1);
  const th = flip ? (pl.a! > 0 ? 1 : -1) * (pl.lim || 20) * DEG : (pl.a || 0) * DEG;
  seesawDraw(ctx, pl.x, pl.y, th, pl.len || 240, off, supFor(pl), sel, g);
}

// Dynamic things (sack, feather, hen)
function dynThing(api: PhysicsApi, pl: Placement, w: number, h: number, opts: Matter.IChamferableBodyDefinition, kind: string,
  drawFn: (ctx: Ctx2D, b: GameBody, t: number, g: Gfx, S: SimState) => void): DynThing {
  const b = api.add(api.R(pl.x, pl.y, w, h, Object.assign({ chamfer: { radius: Math.min(w, h) / 3 } }, opts)));
  b.kind = kind; api.dynamic(b);
  const th: DynThing = { layer: 'mid', b, draw(ctx, t, g, S) { drawFn(ctx, b, t, g, S); } };
  return th;
}

// ---------- parts ----------
const parts = {
  henhouse_seesaw: definePart({
    rects: [{ x: 0, y: -10, w: 240, h: 30 }], rot: 'tilt', defaultA: -20,
    create(api, pl) { curStatics = api.level.statics || []; return seesawCreate(api, pl); },
    draw: seesawPlDraw,
    icon(ctx, g) { ctx.scale(0.42, 0.42); seesawDraw(ctx, 0, 10, -20 * DEG, 240, 0, 60, false, g); },
    blockers(pl) { return [{ x: pl.x, y: pl.y + 30, r: 26 }]; },
    intro: seesawIntro,
  }),
  henhouse_sack: definePart({
    rects: [{ x: 0, y: -4, w: 62, h: 62 }], rot: false,
    create(api, pl) {
      const th = dynThing(api, pl, 56, 46, { density: 0.01, friction: 0.8, restitution: 0, frictionAir: 0.004 }, 'henhouse_sack',
        (ctx, b, t, g) => sackDraw(ctx, b.position.x, b.position.y, b.angle, false, g));
      return th;
    },
    draw(ctx, pl, sel, g) { dashDown(ctx, pl.x, pl.y + 34); sackDraw(ctx, pl.x, pl.y, 0, sel, g); },
    icon(ctx, g) { sackDraw(ctx, 0, 4, 0, false, g); },
    intro: sackIntro,
  }),
  henhouse_feather: definePart({
    rects: [{ x: 0, y: 0, w: 80, h: 30 }], rot: false,
    create(api, pl) {
      const st = dynThing(api, pl, 60, 12, { density: 0.0004, friction: 0.6, restitution: 0, frictionAir: 0.1 }, 'henhouse_feather',
        (ctx, b, t, g) => featherDraw(ctx, b.position.x, b.position.y, b.angle, false, g));
      const b = st.b;
      // sways from side to side while it floats down
      st.step = api => { if (!api.S.started) return; const k = api.S.t; api.Body.applyForce(b, b.position, { x: Math.sin(k * 0.05) * b.mass * 0.00025, y: 0 }); if (api.touching(b).length === 0) api.Body.setAngle(b, Math.sin(k * 0.05) * 0.35); };
      api.onHit(b, o => { if (!o.isSensor) api.sfx('henhouse_fluff', b.position.x, b.position.y, '#FFFFFF', 3); });
      return st;
    },
    draw(ctx, pl, sel, g) { dashDown(ctx, pl.x, pl.y + 20); featherDraw(ctx, pl.x, pl.y, 0, sel, g); },
    icon(ctx, g) { featherDraw(ctx, 0, 0, -0.3, false, g); },
    intro: featherIntro,
  }),
  henhouse_hen: definePart({
    rects: [{ x: 0, y: -4, w: 80, h: 64 }], rot: false,
    create(api, pl) {
      let landed = false;
      const st = dynThing(api, pl, 56, 44, { density: 0.0062, friction: 0.9, restitution: 0, frictionAir: 0.01 }, 'henhouse_hen',
        (ctx, b, t, g) => henDraw(ctx, b.position.x, b.position.y + 4, b.angle, false, g, !landed && api.S.started && b.speed > 0.5, t));
      const b = st.b; api.Body.setInertia(b, Infinity);
      api.onHit(b, o => { if (!landed && !o.isSensor) { landed = true; api.sfx('henhouse_cluck', b.position.x, b.position.y, '#FFFFFF', 6); } });
      return st;
    },
    draw(ctx, pl, sel, g) { dashDown(ctx, pl.x, pl.y + 34); henDraw(ctx, pl.x, pl.y + 4, 0, sel, g, false, 0); },
    icon(ctx, g) { henDraw(ctx, 4, 6, 0, false, g, false, 0); },
    intro: henIntro,
  }),
};

// ---------- objects ----------
const objs = {
  // Hen house roof: nothing can be placed under it
  henhouse_roof: defineObj({
    layer: 'mid',
    create(api, od) { const o = od as WideObj; const b = api.add(api.R(o.x, o.y, o.w, 26, { isStatic: true, friction: 0.3 })); b.kind = 'henhouse_roof_part'; return {}; },
    blockers(od) { const o = od as WideObj; const out = []; for (let x = o.x - o.w / 2 + 20; x <= o.x + o.w / 2 - 10; x += 30) out.push({ x, y: o.y, r: 22 }, { x, y: o.y + 60, r: 30 }, { x, y: o.y + 115, r: 30 }); return out; },
    draw(ctx, st, t, g) {
      const o = st.o as WideObj, x0 = o.x - o.w / 2;
      ctx.save();
      // wall behind the roof (light) + roof with tiles
      ctx.globalAlpha = 0.85; g.rr(ctx, x0 + 10, o.y + 12, o.w - 20, 750 - o.y - 12, 6); ctx.fillStyle = 'rgba(255,170,150,.35)'; ctx.fill(); ctx.globalAlpha = 1;
      g.block(ctx, o.x, o.y, o.w, 26, 0, '#FF8A7A', '#C9443C', 8);
      g.rr(ctx, x0, o.y - 13, o.w, 26, 8); outline(ctx, 2.5);
      ctx.strokeStyle = 'rgba(120,20,20,.4)'; ctx.lineWidth = 2;
      for (let x = x0 + 18; x < x0 + o.w; x += 22) { ctx.beginPath(); ctx.arc(x, o.y - 2, 10, 0, Math.PI); ctx.stroke(); }
      ctx.restore();
    },
  }),
  // Hanging beam (hayloft): nothing can be placed under it
  henhouse_beam: defineObj({
    layer: 'mid',
    create(api, od) { const o = od as WideObj; const b = api.add(api.R(o.x, o.y, o.w, 28, { isStatic: true, friction: 0.3 })); b.kind = 'henhouse_beam_part'; return {}; },
    blockers(od) { const o = od as WideObj; const out = []; for (let x = o.x - o.w / 2; x <= o.x + o.w / 2; x += 35) out.push({ x, y: o.y + 50, r: 30 }); return out; },
    draw(ctx, st, t, g) {
      const o = st.o as WideObj;
      ctx.save(); ctx.strokeStyle = '#8A6A4A'; ctx.lineWidth = 4;
      for (const s of [-1, 1]) { ctx.beginPath(); ctx.moveTo(o.x + s * (o.w / 2 - 14), o.y); ctx.lineTo(o.x + s * (o.w / 2 - 30), 0); ctx.stroke(); }
      baleDraw(ctx, o.x, o.y, o.w, 28, 0, g);
      ctx.restore();
    },
  }),
  // The hen on the perch has just laid the egg (decoration + a small perch)
  henhouse_perch: defineObj({
    layer: 'back',
    create() { return {}; },
    blockers(od) { const o = od as PerchObj; return [{ x: o.x - 60 * (o.dir || 1), y: o.y - 40, r: 40 }]; },
    draw(ctx, st, t, g) {
      const o = st.o as PerchObj, d = o.dir || 1, x = o.x - 62 * d, y = o.y - 14;
      ctx.save();
      // post + perch; the hen sits right next to the egg
      boardDraw(ctx, x - 30 * d, y + (750 - y) / 2, 0, 16, 750 - y, false, g, ['#D8A16A', '#9E6A3C']);
      boardDraw(ctx, x, y, 0, 100, 12, false, g, ['#D8A16A', '#9E6A3C']);
      henDraw(ctx, x - 4, y - 30 + Math.sin(t * 0.08) * 1.5, 0, false, g, false, t);
      ctx.restore();
    },
  }),
};

// ---------- sounds ----------
const sounds: Record<string, SoundFn> = {
  henhouse_thud: (tone, noise) => { noise(0.18, 0.4, 260, 1.2); tone(110, 0.2, 'sine', 0.35, 60); },
  henhouse_clonk: (tone, noise) => { tone(320, 0.08, 'square', 0.12, 240); noise(0.06, 0.2, 1400, 2); },
  henhouse_cluck: (tone) => { tone(520, 0.07, 'triangle', 0.2, 380); tone(470, 0.08, 'triangle', 0.2, 340, 0.12); },
  henhouse_fluff: (tone, noise) => { noise(0.25, 0.08, 3000, 0.7); },
};

// ---------- reskins (kit parts, ball and goal in hen-yard style) ----------
const reskin: WorldDef['reskin'] = {
  plank(ctx, pl, sel, g, rs) { const r = rs[0]; boardDraw(ctx, r.x, r.y, r.a, r.w, r.h, sel, g); },
  long_plank(ctx, pl, sel, g, rs) { const r = rs[0]; boardDraw(ctx, r.x, r.y, r.a, r.w, r.h, sel, g); },
  box(ctx, pl, sel, g, rs) { const r = rs[0]; baleDraw(ctx, r.x, r.y, r.w, r.h, r.a, g); if (sel) { ctx.save(); ctx.translate(r.x, r.y); ctx.rotate(r.a); selBox(ctx, -r.w / 2 - 8, -r.h / 2 - 8, r.w + 16, r.h + 16); ctx.restore(); } },
  stone: pumpkinDraw,
  seesaw(ctx, pl, sel, g) { seesawDraw(ctx, pl.x, pl.y, (pl.a || 0) * DEG, 230, 0, 40, sel, g); },
};
const icons: WorldDef['icons'] = {
  plank(ctx, g) { boardDraw(ctx, 0, 0, -0.35, 100, 18, false, g); },
  long_plank(ctx, g) { boardDraw(ctx, 0, 0, -0.35, 110, 16, false, g); },
  box(ctx, g) { baleDraw(ctx, 0, 0, 56, 70, 0, g); },
  stone(ctx, g) { const pl = { x: 0, y: 0 }; pumpkinDraw(ctx, pl, false, g); },
};

function ball(ctx: Ctx2D, b: GameBody, kind: string, t: number, g: Gfx) { eggDraw(ctx, b, kind, t, g); }

const goal = {
  bowl(ctx: Ctx2D, gl: BowlLook, full: number, t: number, g: Gfx) {
    const { x, y, w } = gl;
    ctx.save();
    if (gl.style === 'tray') {   // cardboard egg tray
      g.shadowOn(ctx, 12, 6);
      ctx.beginPath(); ctx.moveTo(x - w / 2 - 6, y - 50); ctx.lineTo(x - w / 2 + 4, y + 7); ctx.lineTo(x + w / 2 - 4, y + 7); ctx.lineTo(x + w / 2 + 6, y - 50); ctx.closePath();
      ctx.fillStyle = g.lg(ctx, y - 50, y + 7, '#D9CBB8', '#A8977F'); ctx.fill(); g.shadowOff(ctx); outline(ctx, 3);
      ctx.fillStyle = 'rgba(90,70,50,.25)';
      const n = Math.max(3, Math.round(w / 40));
      for (let i = 0; i < n; i++) { ctx.beginPath(); ctx.ellipse(x - w / 2 + (i + .5) * w / n, y - 14, w / n / 2 - 6, 12, 0, 0, 7); ctx.fill(); }
      // the lid stands open on the right
      ctx.save(); ctx.translate(x + w / 2 + 4, y - 50); ctx.rotate(-1.15);
      g.rr(ctx, 0, -6, Math.min(90, w * 0.45), 14, 6); ctx.fillStyle = '#CDBEA9'; ctx.fill(); outline(ctx, 2.5);
      ctx.restore();
      ctx.restore(); return;
    }
    // nest of straw and twigs
    g.shadowOn(ctx, 12, 6);
    ctx.beginPath(); ctx.moveTo(x - w / 2 - 10, y - 50); ctx.quadraticCurveTo(x - w / 2, y + 12, x, y + 10); ctx.quadraticCurveTo(x + w / 2, y + 12, x + w / 2 + 10, y - 50); ctx.closePath();
    ctx.fillStyle = g.lg(ctx, y - 50, y + 10, '#E7B562', '#A9733A'); ctx.fill(); g.shadowOff(ctx); outline(ctx, 3);
    // soft feather lining
    ctx.beginPath(); ctx.ellipse(x, y - 46, w / 2, 9, 0, 0, 7); ctx.fillStyle = '#FFF4E0'; ctx.fill();
    if (full) for (let i = 0; i < 3; i++) { const k = ((t * .5 + i * 40) % 120) / 120; ctx.globalAlpha = 1 - k; featherDraw(ctx, x - 30 + i * 30, y - 70 - k * 60, 0.3 * Math.sin(t * .05 + i), false, { ...g, shadowOn() {}, shadowOff() {} }); ctx.save(); ctx.restore(); }
    ctx.globalAlpha = 1;
    ctx.restore();
  },
  front(ctx: Ctx2D, gl: BowlLook, g: Gfx) {
    const { x, y, w } = gl;
    ctx.save(); ctx.lineCap = 'round';
    if (gl.style === 'tray') { g.rr(ctx, x - w / 2 - 6, y - 52, w + 12, 12, 6); ctx.fillStyle = '#D9CBB8'; ctx.fill(); outline(ctx, 2); ctx.restore(); return; }
    ctx.strokeStyle = '#B98242'; ctx.lineWidth = 6;
    for (let i = 0; i < 6; i++) { const px = x - w / 2 + 4 + i * (w - 8) / 5; ctx.beginPath(); ctx.moveTo(px - 18, y - 46 + (i % 2) * 6); ctx.quadraticCurveTo(px, y - 58, px + 22, y - 44 - (i % 2) * 4); ctx.stroke(); }
    ctx.strokeStyle = '#F2CB7C'; ctx.lineWidth = 3;
    for (let i = 0; i < 5; i++) { const px = x - w / 2 + 14 + i * (w - 20) / 4; ctx.beginPath(); ctx.moveTo(px - 14, y - 50); ctx.quadraticCurveTo(px, y - 58 + (i % 2) * 4, px + 16, y - 48); ctx.stroke(); }
    ctx.restore();
  },
};

// ---------- meta ----------
const meta: WorldMeta = {
  icon: '🐔', strings, music: 'henhouse.mp3',
  credits: { title: 'Autumn on the Farm', artist: 'MoonpetalMedia', id: 587378, url: 'https://pixabay.com/music/folk-autumn-on-the-farm-cozy-whimsical-fall-folk-587378/' },
  synth: { bpm: 86, root: 220.0, sc: 'major', prog: [0, 3, 0, 4], bar: 4, lead: 'pluck', dens: 0.5 },
};

// ---------- world ----------
const world = defineWorld({
  k: K,
  meta,
  scene,
  wash: 'rgba(255,250,236,.5)',
  // statics for the sawhorse height; the goal's style ('tray') for the goal drawing
  setup(api, level) { curStatics = level.statics || []; if (api.S.goal) api.S.goal.style = level.goal!.style; },
  skins,
  parts,
  objs,
  sounds,
  reskin,
  icons,
  ball,
  goal,
  levels,
});
export default world;
